import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface FaqItem {
  question: string;
  answer: string;
  category?: string;
}

/**
 * Customer-facing FAQ. Powered by site_content.faq_items, edited in the admin.
 * Falls back to a tiny built-in set so the page never looks broken.
 */
const FALLBACK: FaqItem[] = [
  {
    category: 'Credits & Payments',
    question: 'Do my credits expire?',
    answer:
      'No. Credits sit in your account until you are ready to use them. There is no expiry date and no monthly charge.'
  },
  {
    category: 'Credits & Payments',
    question: 'Is there a minimum order?',
    answer:
      'A minimum spend of 3 credits is required per checkout, except where you are using refunded credits from a fit return.'
  },
  {
    category: 'Rental & Returns',
    question: 'How long is the rotation?',
    answer:
      '30 days from the day your delivery is confirmed. We email a prepaid return label between Day 25 and 30.'
  }
];

export function Faq() {
  const [items, setItems] = useState<FaqItem[] | null>(null);
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});

  useEffect(() => {
    apiFetch<Record<string, { items?: FaqItem[] }>>('/api/catalog/site-content')
      .then((all) => {
        const live = all.faq_items?.items;
        setItems(Array.isArray(live) && live.length ? live : FALLBACK);
      })
      .catch(() => setItems(FALLBACK));
  }, []);

  // Group questions by category. Items without a category go under "GENERAL".
  const grouped = useMemo(() => {
    const list = items ?? [];
    const out: { category: string; items: FaqItem[] }[] = [];
    const map = new Map<string, FaqItem[]>();
    for (const it of list) {
      const cat = (it.category ?? '').trim() || 'GENERAL';
      if (!map.has(cat)) {
        map.set(cat, []);
        out.push({ category: cat, items: map.get(cat)! });
      }
      map.get(cat)!.push(it);
    }
    return out;
  }, [items]);

  // Default-open the first category once data lands.
  useEffect(() => {
    if (grouped.length && Object.keys(openCats).length === 0) {
      setOpenCats({ [grouped[0].category]: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped]);

  return (
    <section className="screen">
      <div className="scroll hiw-scroll">
        <div className="hiw-hero">
          <div className="hiw-eyebrow">Help centre</div>
          <h1>FAQ</h1>
          <p>Quick answers to the most common questions.</p>
        </div>

        <section className="hiw-faq-section" style={{ paddingTop: 0 }}>
          {items === null ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-soft)' }}>
              Loading…
            </div>
          ) : grouped.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-soft)' }}>
              No questions published yet.
            </div>
          ) : (
            grouped.map((cat) => {
              const open = !!openCats[cat.category];
              return (
                <div key={cat.category} className="faq-group">
                  <button
                    className={`faq-cat-btn${open ? ' open' : ''}`}
                    onClick={() =>
                      setOpenCats((s) => ({ ...s, [cat.category]: !s[cat.category] }))
                    }
                    type="button"
                  >
                    <span className="l">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 4M12 17h0" />
                      </svg>
                      {cat.category.toUpperCase()}
                    </span>
                    <span className="chev">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </button>
                  <div className={`faq-body${open ? ' open' : ''}`}>
                    <div style={{ height: 4 }} />
                    {cat.items.map((it, j) => (
                      <div key={`${cat.category}-${j}`} className="faq-item">
                        <div className="faq-q">{it.question}</div>
                        <div className="faq-a" style={{ whiteSpace: 'pre-wrap' }}>
                          {it.answer}
                        </div>
                      </div>
                    ))}
                    <div style={{ height: 4 }} />
                  </div>
                </div>
              );
            })
          )}
        </section>

        <div className="hiw-help-bar">
          <div>
            <div className="t">STILL NEED HELP?</div>
            <a className="em" href="mailto:team@labelrotation.co.uk">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
              team@labelrotation.co.uk
            </a>
          </div>
          <Link to="/browse" className="hiw-browse-btn">
            BROWSE PIECES
          </Link>
        </div>
      </div>
    </section>
  );
}
